import React, { useEffect, useRef, useState } from 'react'
import CustomDialog from '../../../components/CustomDialog'
import SaveLoading from '../../../components/SaveLoading'
import SelectBox from '../../../components/SelectBox'
import { FaEdit } from 'react-icons/fa'

const actionCopy = {
  approve: {
    title: 'Approve Transaction',
    highlight: 'Approving this request will instantly release the balance to the user.',
    toneClasses: 'text-customDarkGreen',
    proceedLabel: 'Proceed',
  },
  reject: {
    title: 'Reject Transaction',
    highlight: 'Rejecting this request will revert the balance and notify the user.',
    toneClasses: 'text-customFontRed',
    proceedLabel: 'Proceed',
  },
}

const ActionConfirmationDialog = ({
  open,
  type,
  transaction,
  transactionNo,
  note,
  onNoteChange,
  onNoChange,
  amount,
  onAmountChange,
  onProceed,
  onCancel,
  saveLoading,
  isMobile,
  bankAccounts,
  selectedAccount,
  setSelectedAccount,
  error
}) => {
  const [isEditingAmount, setIsEditingAmount] = useState(false)
  const amountInputRef = useRef(null)

  useEffect(() => {
    console.log("transaction", transaction?.type)
    setIsEditingAmount(false)
  }, [open, transaction?.id])

  useEffect(() => {
    if (isEditingAmount && amountInputRef.current) {
      amountInputRef.current.focus()
    }
  }, [isEditingAmount])

  if (!type || !transaction) return null

  const copy = actionCopy[type]
  const longText = 'ကျေးဇူးပြု၍ အောက်ပါ ငွေပေးငွေယူအသေးစိတ်ကို သေချာစစ်ဆေးပါ။ ငွေပေးချေမှု ရည်ညွှန်းချက်များသည် သင်အတည်ပြုခြင်းမပြုမီ သင်၏မိုဘိုင်းငွေပေးချေမှု မှတ်တမ်းများနှင့် ကိုက်ညီကြောင်း သေချာပါစေ။ ဤလုပ်ဆောင်ချက်ကို လိုက်နာမှုအတွက် မှတ်တမ်းတင်ထားပြီး ပြန်ပြင်၍မရပါ။'
  //const longText =
  //  'Please double-check the transaction details below. Make sure the payment references match internal records before you confirm. This action is logged for compliance and cannot be undone.'

  const hasCustomAmount = amount !== undefined && amount !== null && amount !== ''
  const parsedAmount = hasCustomAmount ? Number(amount) : null
  const resolvedAmount =
    hasCustomAmount && Number.isFinite(parsedAmount) ? parsedAmount : transaction.amount

  const formatCurrency = (value) =>
    value.toLocaleString(undefined, {
      style: 'currency',
      currency: transaction.currency,
    })

  const formattedAmount = formatCurrency(resolvedAmount)

  const handleAmountInputChange = (event) => {
    onAmountChange?.(event.target.value)
  }

  const stopEditingAmount = () => {
    setIsEditingAmount(false)
  }

  const handleAmountKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === 'Escape') {
      stopEditingAmount()
    }
  }

  return (
    <CustomDialog
      open={open}
      width={isMobile ? 340 : 520}
      padding={isMobile ? 3 : 4}
      isMobile={isMobile}
      extraStyle={{ borderRadius: 10, border: '1px solid #6574FF' }}
    >
      <div className="relative">
        {saveLoading ? <SaveLoading loading={saveLoading} /> : null}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-customPrimaryText dark:text-customHeading">
            {copy.title}
          </h2>
          <span
            className={`rounded-full bg-customLightMilkWhite px-3 py-1 text-xs font-semibold uppercase tracking-wide dark:bg-customSurfaceMuted ${copy.toneClasses}`}
          >
            {transaction.type}
          </span>
        </div>
        <p className="mb-3 text-sm leading-relaxed text-customSecondaryText dark:text-customMuted">
          {longText}
        </p>
        <div
          className={`mb-4 rounded-xl border border-dashed border-customAccent/40 bg-customAccent/10 p-3 text-sm font-medium ${copy.toneClasses}`}
        >
          {copy.highlight}
        </div>
        <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg bg-customLightMilkWhite/80 p-3 text-sm text-customPrimaryText dark:bg-customSurfaceMuted dark:text-customLightMilkWhite">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
              Transaction No
            </span>
            <span className="font-semibold text-customAccentBlue">
              {transaction.transactionNo}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-customSecondaryText dark:text-customMuted">Username</span>
            <span className="font-semibold">{transaction.username}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-customSecondaryText dark:text-customMuted">Amount</span>
            <div className="flex items-center gap-1">
              {isEditingAmount ? (
                <input
                  ref={amountInputRef}
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount ?? ''}
                  onChange={handleAmountInputChange}
                  onBlur={stopEditingAmount}
                  onKeyDown={handleAmountKeyDown}
                  className="w-24 rounded border border-customAccent/40 bg-white px-2 py-1 text-right text-xs font-semibold text-customPrimaryText outline-none focus:border-customAccent focus:ring-1 focus:ring-customAccent/40 dark:border-white/10 dark:bg-customSurface dark:text-customLightMilkWhite"
                />
              ) : (
                <span className="font-semibold text-customFontVoilet">
                  {formattedAmount}
                </span>
              )}
              {transaction.type === "deposit" &&
                <button
                  type="button"
                  aria-label="Edit amount"
                  className="text-customSecondaryText transition hover:text-customAccent"
                  onClick={() => setIsEditingAmount(true)}
                >
                  <FaEdit size={16} />
                </button>
              }
            </div>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-customSecondaryText dark:text-customMuted">Submitted on</span>
            <span className="font-semibold">{transaction.createdDate}</span>
          </div>
        </div>
        {
          transaction.type === 'withdraw' && type === 'approve' && bankAccounts.length > 0 && (
            <div className={`flex ${isMobile ? 'flex-col' : 'gap-2 items-center'} mb-4`}>
              <div className={`${isMobile ? 'w-full mb-3' : 'w-[60%]'}`}>
                <SelectBox
                  selected={selectedAccount}
                  setSelected={setSelectedAccount}
                  options={bankAccounts}
                  isMobile={isMobile}
                />
              </div>
              <div className={`${isMobile ? 'w-full' : 'w-[40%]'}`}>
                <input
                  type="text"
                  value={transactionNo}
                  onChange={(event) => onNoChange(event.target.value)}
                  placeholder="Last 6 transaction no .. "
                  className="mt-1 w-full rounded-lg border border-customBorder/40 bg-white px-3 py-2 text-sm text-customPrimaryText outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-white/10 dark:bg-customSurface dark:text-customLightMilkWhite"
                />
              </div>
            </div>)
        }
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
          Operator note
        </label>
        <input
          type="text"
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder="Add optional reference or note for audit trail"
          className="mb-3 w-full rounded-lg border border-customBorder/40 bg-white px-3 py-2 text-sm text-customPrimaryText outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-white/10 dark:bg-customSurface dark:text-customLightMilkWhite"
        />
        {
          error ? <p className="mb-2 text-sm font-medium text-customFontRed text-center">{error}</p> : null
        }
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border bg-customBackColor border-customSecondaryText/20 px-4 py-2 text-sm font-semibold text-customSecondaryText transition hover:border-customAccent hover:text-customAccent dark:border-white/10 dark:text-customMuted dark:hover:border-customAccent dark:hover:text-customAccent"
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onProceed}
            className={`rounded-lg px-5 py-2 text-sm font-semibold text-customWhite transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${type === 'approve'
              ? 'bg-customDarkGreen focus:ring-customDarkGreen/50'
              : 'bg-customFontRed focus:ring-customFontRed/50'
              }`}
            disabled={saveLoading}
          >
            {copy.proceedLabel}
          </button>
        </div>
      </div>
    </CustomDialog>
  )
}

export default ActionConfirmationDialog
